// frontend/packages/business-console/src/pages/App.js

import React, { useEffect, useMemo, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Box, CircularProgress, Container, Typography } from '@mui/material';

import Layout from '../components/layout/Layout';

import { useAuth } from '../contexts/AuthContext';
import { BusinessProvider } from '../contexts/BusinessContext';
import { InboxUnreadProvider } from '../contexts/InboxUnreadContext';

import Login from './Login';
import BuildWelcome from './BuildWelcome';
import Logout from './Logout';
import Unauthorized from './Unauthorized';
import SentryDebug from './SentryDebug';
import StaffLanding from './StaffLanding';

import { getSlugByBusinessId } from '../services/businessService';
import { Workspace_Pages } from './pageConfig';
import DistrictModeGate from '../components/layout/DistrictModeGate';

const FullPageSpinner = ({ label = 'Loading…' }) => (
  <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center', p: 3 }}>
    <Box sx={{ textAlign: 'center' }}>
      <CircularProgress />
      <Typography variant="body2" sx={{ mt: 2, opacity: 0.75 }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <FullPageSpinner label="Preparing your session…" />;
  if (!user) return <Navigate to="/login" replace />;

  return children;
};

const LegacyBusinessIdRedirect = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        if (!businessId) {
          setStatus('not-found');
          return;
        }

        const { data } = await getSlugByBusinessId(businessId);
        const slug = data?.slug;
        if (!slug) {
          setStatus('not-found');
          return;
        }

        const base = `/business/${businessId}`;
        const remainder = location.pathname.startsWith(base)
          ? location.pathname.slice(base.length)
          : '';

        const next = `/business/${slug}${remainder}${location.search}${location.hash}`;
        if (alive) navigate(next, { replace: true });
      } catch {
        if (alive) setStatus('not-found');
      }
    })();

    return () => {
      alive = false;
    };
  }, [businessId, location.hash, location.pathname, location.search, navigate]);

  if (status === 'loading')
    return <FullPageSpinner label="Opening your workspace…" />;

  return (
    <Container sx={{ mt: 6, textAlign: 'center' }}>
      <Typography variant="h6">Business not found.</Typography>
    </Container>
  );
};

const RedirectToWorkspace = () => {
  const { user, loading, businessId, isStaff } = useAuth();
  const navigate = useNavigate();

  const resolvedBusinessId = useMemo(() => {
    return businessId || user?.activeBusinessId || user?.businessId || null;
  }, [businessId, user]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (loading) return;

      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      if (!resolvedBusinessId) {
        // Staff users without a selected client land on the picker, not
        // /unauthorized — having no businessAccess is normal for them.
        navigate(isStaff ? '/staff' : '/unauthorized', { replace: true });
        return;
      }

      try {
        const { data } = await getSlugByBusinessId(resolvedBusinessId);
        const slug = data?.slug;
        if (!slug) {
          navigate('/unauthorized', { replace: true });
          return;
        }

        if (alive) navigate(`/business/${slug}/home`, { replace: true });
      } catch {
        if (alive) navigate('/unauthorized', { replace: true });
      }
    })();

    return () => {
      alive = false;
    };
  }, [loading, navigate, resolvedBusinessId, user]);

  return <FullPageSpinner label="Opening your workspace…" />;
};

const BusinessWorkspaceRoutes = () => {
  const { slug } = useParams();

  return (
    <RequireAuth>
      <BusinessProvider slug={slug}>
        <InboxUnreadProvider>
          <Layout>
            <Routes>
              {Workspace_Pages.map((page) => {
                if (page.key === 'home') {
                  return <Route key={page.key} index element={page.element} />;
                }

                const element = (
                  <DistrictModeGate pageKey={page.key}>
                    {page.element}
                  </DistrictModeGate>
                );
                return (
                  <Route key={page.key} path={page.path} element={element} />
                );
              })}
              <Route
                path="home"
                element={<Navigate to={`/business/${slug}`} replace />}
              />
              <Route path="logout" element={<Logout />} />
              <Route
                path="*"
                element={<Navigate to={`/business/${slug}/home`} replace />}
              />
            </Routes>
          </Layout>
        </InboxUnreadProvider>
      </BusinessProvider>
    </RequireAuth>
  );
};

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/build/welcome" element={<BuildWelcome />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/sentry-debug" element={<SentryDebug />} />
        {/* Staff picker. Standalone (no BusinessProvider/Layout) so the
            sidebar's per-business widgets don't get stuck in their loading
            state. The ClientSwitcher inside the page is the only nav
            staff need here. */}
        <Route
          path="/staff"
          element={
            <RequireAuth>
              <StaffLanding />
            </RequireAuth>
          }
        />

        <Route
          path="/business/:businessId([0-9a-fA-F]{24})/*"
          element={<LegacyBusinessIdRedirect />}
        />

        <Route path="/business/:slug/*" element={<BusinessWorkspaceRoutes />} />

        <Route path="*" element={<RedirectToWorkspace />} />
      </Routes>
    </Router>
  );
}
